<template>
	<div id="all-funds" :class="$store.state.audience">
		<article>
			<page-banner :widget-data="pageBannerWidgetData" />
		</article>
		<article>
			<secondary-nav :site-navigation="secondaryNavContent" @nav-click="linkClick" />
		</article>
		<article>
			<div class="returns-main">
				<!-- CG-23186: display this message until AFIS Share Class 3 launch -->
				<div v-if="!afisShareClass3Enabled" class="yellow-alert-box">
					<strong>Notice:</strong>
					<span class="non-bold">
						Click <a :href="qsuPdfLink" target="_blank">here</a> to view American Funds Insurance Series Share Class 3 returns.
					</span>
				</div>
				<search-filters :widget-data="returnsFiltersData" @update-filters="updateFilters" />
				<div>
					<h3 class="com-mod-section-header">
						{{ numberOfResults }}
					</h3>
					<read-more />
					<setting-filters :widget-data="settingFilterWidgetData" @toggle-time-span="toggleTimeSpan"
						@toggle-show-indices="toggleShowIndices" @update-share-class="updateSelectedShareClass">
						<template #Iball_shareclass>
							<i-ball-modal tag="ShareClass" />
						</template>
					</setting-filters>

					<returns-table :widget-data="widgetData">
						<template v-for="(indexId, index) in indexSuperscripts" :key="`i-ball${index}`"
							#[`i_ball_index${indexId}`]>
							<i-ball-modal :tag="indexId" boundary="#returns-table-wrapper" />
						</template>
						<template v-for="(indexId, index) in indexSuperscripts" :key="`disclosure_${index}`"
							#[`disclosure_index${indexId}`]>
							<disclosure-superscript :statistics="[`${indexId}`]" :anchor="`disclosure_index${indexId}`"
								class="returns-page" />
						</template>
						<template #returnsYTD>
							<disclosure-superscript class="returns-page" anchor="returnsYTD"
								:statistics="['NAVYTDDaily']" />
						</template>
						<template #returnsYTD1yr>
							<disclosure-superscript class="returns-page" anchor="returnsYTD1yr"
								:statistics="['NAVCumul1Yr']" />
						</template>
						<template #returnsYTD3yr>
							<disclosure-superscript class="returns-page" anchor="returnsYTD3yr"
								:statistics="['NAVAvg3yr']" />
						</template>
						<template #returnsYTD5yr>
							<disclosure-superscript class="returns-page" anchor="returnsYTD5yr"
								:statistics="['NAVAvg5yr']" />
						</template>
						<template #returnsYTD10yr>
							<disclosure-superscript class="returns-page" anchor="returnsYTD10yr"
								:statistics="['NAVAvg10yr']" />
						</template>
						<template #returnsYTD15yr>
							<disclosure-superscript class="returns-page" anchor="returnsYTD15yr"
								:statistics="['ARRNAV_15YTD']" />
						</template>
						<template #returnsExpenseRatio>
							<disclosure-superscript class="returns-page" anchor="returnsExpenseRatio"
								:statistics="['expRatio']" />
						</template>
					</returns-table>
				</div>
			</div>
		</article>
	</div>
</template>

<script>
import PageBanner from '_components_/Shared/PageBanner.vue';
import SecondaryNavigation from '_components_/ReturnsComponents/SecondaryNavigation.vue';
import ReadMoreLink from '_components_/Shared/ReadMoreLink';
import ReturnsTable from '_components_/ReturnsComponents/ReturnsTable';
import SettingFilters from '_components_/ReturnsComponents/SettingFilters';
import SearchFilters from '_components_/ReturnsComponents/SearchFilters';
import { mapGetters } from 'vuex';
import SelectionsMixin from '_mixins_/SelectionsMixin';

export default {
	name: 'ReturnsPage',
	components: {
		'page-banner': PageBanner,
		'secondary-nav': SecondaryNavigation,
		'search-filters': SearchFilters,
		'returns-table': ReturnsTable,
		'setting-filters': SettingFilters,
		'read-more': ReadMoreLink
	},
	mixins: [SelectionsMixin],
	computed: {
		...mapGetters([
			'appConfig',
			'getAudience',
			'getBaseUrl',
			'getReturnsPageData',
			'returnsFiltersData',
			'strategies',
			'universeAvailableShareClasses',
			'getEnvironment'
		]),
		secondaryNavContent() {
			const isProd = this.getEnvironment === 'production';
			const content = [{
				key: 'American Funds Insurance Series',
				subTitle: 'Insurance Funds',
				navButtons: [
					{
						title: 'Returns',
						//Capital group controls the whole page for the 'Prospectuses & Reports' content, so no link is needed for this button as its always active when on the Markit Digital content
						link: '',
						isActive: true
					}
				]
			}];
			if (this.getAudience === 'individual') {
				content[0].navButtons.push(
					{
						title: 'Daily Prices & Yields',
						link: isProd
							? `https://www.capitalgroup.com/individual/investments/insurance-funds/daily-prices.htm?shareClass=${this.selectedShareClass}`
							: `https://www.stg.capitalgroup.com/individual/investments/insurance-funds/daily-prices.htm?shareClass=${this.selectedShareClass}`,
						isActive: false
					},
					{
						title: 'Prospectuses & Reports',
						link: isProd
							? 'https://www.capitalgroup.com/individual/investments/insurance-funds/prospectuses-and-reports.htm'
							: 'https://www.stg.capitalgroup.com/individual/investments/insurance-funds/prospectuses-and-reports.htm',
						isActive: false
					}
				);
			} else { //getAudience === 'advisor'
				content[0].navButtons.push({
					title: 'Daily Prices & Yields',
					link: isProd
						? `https://www.capitalgroup.com/advisor/investments/insurance-funds/daily-prices.htm?shareClass=${this.selectedShareClass}`
						: `https://www.stg.capitalgroup.com/advisor/investments/insurance-funds/daily-prices.htm?shareClass=${this.selectedShareClass}`,
					isActive: false
				});
				content[0].navButtons.push({
					title: 'Share Class Information',
					link: 'https://www.capitalgroup.com/advisor/investments/insurance-funds/share-class-information.html',
					isActive: false
				});
			}
			return content;
		},
		investmentsLink() {
			return `${this.getBaseUrl}/${this.getAudience}/investments`;
		},
		settingFilterWidgetData() {
			return {
				showIndices: this.showIndices,
				timeSpan: this.timeSpan,
				//the selected share class matches the hardcoded value used in the initial call map in App.vue
				selectedShareClass: this.selectedShareClass,
				shareClasses: this?.universeAvailableShareClasses?.map(obj => obj.ShareClass) ?? []
			};
		},
		pageBannerWidgetData() {
			return {
				title: 'Returns',
				shouldDisplayNavComponents: false,
				link: this.investmentsLink,
				audience: this.getAudience
			};
		},
		returnsPageData() {
			return this.getReturnsPageData || {};
		},
		numberOfResults() {
			const length = this.getTableData.reduce((acc, cur) => {
				return [...acc, ...cur.value];
			}, []).length;
			return `${length} American Fund${length === 1 ? '' : 's'}`;
		},
		getTableData() {
			const formattedTableData = [];
			let data = [];
			if (this.returnsPageData.returns) {
				if (this.timeSpan === 'monthly') {
					data = this.returnsPageData.returns.monthly;
				} else {
					data = this.returnsPageData.returns.quarterly;
				}
				const objectives = new Set(Array.from(data, k => k['objective']));

				const that = this;
				objectives.forEach(objective => {
					//only include objectives that are included in the selected list, or all of them is the list is empty
					if (that.selectedObjectives.length && that.selectedObjectives.includes(objective) || !that.selectedObjectives.length) {
						const objectiveWithEntries = {
							key: objective,
							value: data.filter(item => {
								//filter by appending entries that have a matching:
								//objective in the entry = current unique objective
								const matchingObjective = item['objective'] === objective;

								//the asset type is included in the list of currently filtered asset types/ or if the asset types list is empty
								const includedInFilteredAssetTypes = that.selectedAssetTypes.length ? that.selectedAssetTypes.includes(item.assetType) : true;

								//the share class matches the currently selected share class
								const shareClassMatchesSelected = this.selectedShareClass === item.shareClass;

								return matchingObjective && includedInFilteredAssetTypes && shareClassMatchesSelected;
							})
						};
						//after applying the filters, only append the object and its entries, if there ARE entries
						if (objectiveWithEntries.value.length) {
							//append the URLName value from the fundUniverse data to be used in the URL generation that links to the detail page for each fund
							objectiveWithEntries.value.forEach(fund => {
								const match = this.strategies.filter(entry => entry.FundName === fund.fundName);
								//multiple values may be found (for each possible share class) but the ULR name is the same for all. The share class used is based on the selected share class
								fund['URLName'] = match[0] && `${match[0].URLName}-${this.selectedShareClass.toLowerCase()}`;
								//CG-23156: use the Quarterly Index for Quarterly Returns
								if (this.timeSpan === 'quarterly') {
									fund.indexId = fund.indexQtrId ?? fund.indexId;
									fund.indexName = fund.indexQtrName ?? fund.indexName;
								}
							});
							formattedTableData.push(objectiveWithEntries);
						}
					}
				});
			}
			this.setFundAbbreviations(formattedTableData);
			return formattedTableData;
		},
		indexSuperscripts() {
			const indices = [];
			this.getTableData.forEach(objective => {
				objective.value.forEach(data => {
					indices.push(data.indexId);
				});
			});
			return [...new Set(indices)];
		},
		returnsDates() {
			const dateObj = {};
			if (this.returnsPageData && Object.keys(this.returnsPageData).length > 0) {
				const dates = this.returnsPageData.dates[this.selectedShareClass];
				dateObj['DailyYTDReturnAsOfDate'] = dates.DailyYTDReturnAsOfDate;
				dateObj['YTDReturnAsOfDate'] = dates.MonthlyYTDReturnAsOfDate;
				dateObj['ReturnsDateMonthly'] = dates.MonthlyAsOfDate;
				dateObj['ReturnsDateQuarterly'] = dates.QtrAsOfDate;
				dateObj['ReturnsDateYTDMonthly'] = dates.MonthlyAsOfDate;
				dateObj['ReturnsDateYTDQuarterly'] = dates.QtrAsOfDate;
			}
			return dateObj;
		},
		widgetData() {
			if (this.returnsPageData && Object.keys(this.returnsPageData).length > 0) {
				return {
					dates: this.returnsDates,
					objectives: this.returnsPageData.objectives || [],
					selectedObjectives: this.selectedObjectives,
					selectedAssetTypes: this.selectedAssetTypes,
					tableData: this.getTableData,
					showIndices: this.showIndices,
					timeSpan: this.timeSpan,
					shareClasses: this.universeAvailableShareClasses
				};
			}
			return {};
		}
	},
	watch: {
		universeAvailableShareClasses(val) {
			const shareClasses = val?.map(obj => obj.ShareClass) ?? [];
			// when we have the available share class list, verify if the selected shareClass is in the list, and if it is NOT
			//then set the selectedShareClass to "1"
			//this could happen if the app context sends a share class that is not available like "insurance-funds" for Returns page
			if (shareClasses.length && !shareClasses.includes(this.selectedShareClass)) this.selectedShareClass = '1';
		},
		showIndices(val) {
			if (val) this.$store.commit('SET_RETURNS_PAGE_INDEX_IDS', this.indexSuperscripts);
			else this.$store.commit('SET_RETURNS_PAGE_INDEX_IDS', []);
		}
	},
	methods: {
		linkClick(page) {
			/*
			currently nothing needs to happen when clicking the links within the secondary nav.
			This is because the active link is "Returns" and the other links, "Daily Prices & Yields" and "Prospectuses & Reports" (when "individual"),
			link to a page controlled by Capital Group. If another, Markit Digital link is added, rendering new content within this same Returns page
			then the "secondaryNavContent" data would need to be updated to reflect the active link/tab
			*/
		},
		setFundAbbreviations(tableData) {
			const abbreviations = [];
			tableData.forEach(objective => {
				objective.value.forEach(data => {
					abbreviations.push(data.fundAbbreviatedName);
				});
			});
			this.$store.commit('SET_RETURNS_PAGE_FUND_ABBREVIATIONS', [...new Set(abbreviations)]);
		},
		updateSelectedShareClass(shareClass) {
			this.selectedShareClass = shareClass;
			this.updateShareClassStorage();
			this.updateURL();
		},
		updateFilters(val) {
			//defaulting the share class based on objective: CG-16722
			const wasManagedRiskOrPortfolioSeries = (this.selectedObjectives.includes('Managed Risk') || this.selectedObjectives.includes('Portfolio Series'));
			const isAddingManagedRiskOrPortfolioSeries = (val.objectives.value.includes('Managed Risk') || val.objectives.value.includes('Portfolio Series'));
			if (!wasManagedRiskOrPortfolioSeries && isAddingManagedRiskOrPortfolioSeries) {
				this.selectedShareClass = 'P2';
			}

			// default share class to 1 when Retirement Target Date is added to Objective or Target Date is added to Asset Type filter: CG-16969 (share class 4); CG-21953 (share class 1)
			const wasTargetDate = this.selectedObjectives.includes('Retirement Target Date') || this.selectedAssetTypes.includes('Target Date');
			const isAddingTargetDate = val.objectives.value.includes('Retirement Target Date') || val.assetTypes.value.includes('Target Date');
			if (!wasTargetDate && isAddingTargetDate) {
				this.selectedShareClass = '1';
			}
			this.selectedObjectives = val.objectives.value.length ? val.objectives.value : [];
			this.selectedAssetTypes = val.assetTypes.value.length ? val.assetTypes.value : [];
			this.updateURL();
		},
		toggleTimeSpan(val) {
			this.timeSpan = val;
			this.updateURL();
		},
		toggleShowIndices() {
			this.showIndices = !this.showIndices;
			this.updateURL();
		},
		updateShareClassStorage() {
			//Set localStorage Values
			if (this.getAudience === 'advisor') localStorage.setItem('advisor-afis-shareclass', this.selectedShareClass);
			if (this.getAudience === 'individual') localStorage.setItem('individual-afis-shareclass', this.selectedShareClass);
			//Set User.Pref Values via F2 Event
			F2.Events.emit('user_preference_selections', JSON.stringify({ shareClass: this.selectedShareClass, audience: this.getAudience }));
		},
		updateURL() {
			const currentFilters = {
				objectives: this.selectedObjectives.join(',').replace(/\s+/g, '_'),
				assetTypes: this.selectedAssetTypes.join(',').replace(/\s+/g, '_'),
				shareClass: this.selectedShareClass,
				timeSpan: this.timeSpan,
				showIndices: this.showIndices ? 'true' : 'false'
			};
			F2.Events.emit('returns_page_current_filters', JSON.stringify(currentFilters));

			//CG-16568: calling with no payload will fire the F2 event with the same disclosure data
			this.$EventBus.$emit('additional-disclosure-mounted', true);

			let params = [];
			if (this.selectedObjectives?.length) params.push(`objectives=${this.selectedObjectives.join(',').replace(/\s/g, '_')}`);
			if (this.selectedAssetTypes?.length) params.push(`assetTypes=${this.selectedAssetTypes.join(',').replace(/\s/g, '_')}`);
			if (this.selectedShareClass) params.push(`shareClass=${this.selectedShareClass}`);
			if (this.timeSpan) params.push(`timeSpan=${this.timeSpan}`);
			if (this.showIndices) params.push(`showIndices=${this.showIndices}`);
			if (params.length) params = `?${params.join('&')}`;
			history.pushState({}, '', `${window.location.pathname}${params}`);
		}
	}
};
</script>
<style lang="less">
#app {
	#all-funds {
		width: 100%;
		background-color: #f2f2f2;

		.hardcoded_Banner-Returns {
			//see \AmericanFunds\Content\Styles\Common.less
			background-color: rgb(253, 246, 209);
			border: 1px solid rgb(252, 218, 180);
			padding: 8px 12px;
			margin-top: 13px;
			margin-bottom: 13px;
		}

		*:not('#returns-table-wrapper') {
			section {
				padding: 0;

				h3 {
					margin: 10px 0;
					font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
					font-size: 21px;
				}

				a {
					color: #1d5799;
					display: block;
					font-family: Avenir Next LT W01 Demi, Arial, sans-serif;
					font-size: 14px;
					font-weight: 700;
					margin-bottom: 20px;
				}
			}
		}

		//Style overrides for the individual page header
		//I put these in this file because it's specific to returns
		//Todo: abstract these out if it becomes applicable to more than returns page
		&.individual {
			section {
				h3 {
					font-family: AvenirNextLTW01-Regular, Arial, sans-serif;
					font-size: 26px;
					font-weight: 500;
				}
			}

			#top-header #page-banner .title-container .title {
				p:first-of-type {
					font-size: 48px;
				}

				h2 {
					margin-bottom: 20px;
				}
			}
		}
	}

	.returns-main {
		background-color: #fff;
		margin: auto;
		padding: 13px;
		padding-top: 30px;
		min-height: 400px;
		max-width: 996px;

		.yellow-alert-box {
			background-color: #fffcd5;
			margin-bottom: 15px;
			padding: 10px;
			.non-bold {
				font-family: "AvenirNextLTW01-Regular", Arial, sans-serif;
			}
		}

		table {
			margin-bottom: 0px;
		}

		//this is an override, that will show the share class dropdown in the select filter component
		.returns-filter {
			.settings-filter-content {
				.share-class {
					display: inline-block;
				}
			}
		}
	}

	@media (max-width: 768px) {
		.returns-main {
			width: 100%;
		}
	}

	@media (min-width: 996px) {
		#returns-table-wrapper {
			overflow-x: hidden;
		}
	}
}
</style>
